import { noop } from "lodash";
import { useIsConnectEnabled } from "@/react/hooks/useIsConnectEnabled";
import { isIntendedOnNewTab } from "@circle-react/helpers/browserHelpers";
import type { Notification } from "@circle-react/types/Notification";
import { usePopoverPortalContext } from "@circle-react-shared/uikit/PopoverPortal";
import { useNotificationContext } from "../NotificationContext";
import { useNotificationsActions } from "../useHeaderNotificationsApi";
import { NOTIFICATION_ACTION_MAPPER } from "./Item/constants";
import { CommentNotification } from "./NotifiableTypes/CommentNotification";
import { ConnectRequestNotification } from "./NotifiableTypes/ConnectRequestNotification";
import { ContactNotification } from "./NotifiableTypes/ContactNotification";
import { DefaultNotification } from "./NotifiableTypes/DefaultNotification";

export const NotificationItem = ({
  notification,
  lastMarkedAsRead,
}: {
  notification: Notification;
  lastMarkedAsRead: number | null;
}) => {
  const { onClose = noop } = usePopoverPortalContext() || {};
  const { onClose: onCloseDrawer } = useNotificationContext();
  const { mutate: performNotificationAction } = useNotificationsActions();
  const isConnectEnabled = useIsConnectEnabled();

  const notificationCreationTimeStamp = new Date(
    notification.created_at,
  ).getTime();
  const isMarkedAsRead = Boolean(
    lastMarkedAsRead && notificationCreationTimeStamp < lastMarkedAsRead,
  );

  const onClickHandler = (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    performNotificationAction({
      id: notification.id,
      action: NOTIFICATION_ACTION_MAPPER.markAsRead,
    });
    if (isIntendedOnNewTab(event)) {
      return;
    }
    onCloseDrawer ? onCloseDrawer() : onClose();
  };

  const isContactNotification = notification.notifiable_type === "Contact";
  const isCommentNotification = notification.notifiable_type === "Comment";
  const shouldOpenPostInModal =
    notification.notifiable_type === "Post" &&
    (notification.action === "like" || notification.action === "add");

  if (isCommentNotification || shouldOpenPostInModal) {
    return (
      <CommentNotification
        notification={notification}
        onClickHandler={onClickHandler}
        isMarkedAsRead={isMarkedAsRead}
        shouldFocusOnCommentsSection={isCommentNotification}
      />
    );
  }

  if (isContactNotification) {
    return (
      <ContactNotification
        notification={notification}
        onClickHandler={onClickHandler}
        isMarkedAsRead={isMarkedAsRead}
      />
    );
  }

  if (
    isConnectEnabled &&
    notification.notifiable_type === "Connect::Connection"
  ) {
    if (notification.action === "connect_pending_request") {
      return (
        <ConnectRequestNotification
          notification={notification}
          isMarkedAsRead={isMarkedAsRead}
        />
      );
    }

    return (
      <ContactNotification
        notification={notification}
        onClickHandler={onClickHandler}
        isMarkedAsRead={isMarkedAsRead}
      />
    );
  }

  return (
    <DefaultNotification
      notification={notification}
      onClickHandler={onClickHandler}
      isMarkedAsRead={isMarkedAsRead}
    />
  );
};
